/**
 * System / governance API service (S10.1). Thin apiClient wrappers over the
 * MSW governance endpoints — kept slice-local (the codex governance.api.ts is
 * outside the owned set and backs the retired page).
 */

import apiClient from '@/services/api/axios.config';
import type {
  AuditLogQuery,
  GovernanceAuditLog,
  GovernanceListResponse,
  GovernanceRole,
  GovernanceUser,
  OrganisationUnit,
  OrgKind,
  OrgModel,
  GovernanceRecordStatus,
  GovernanceUserStatus,
  RoleLevel,
} from './model';

const BASE = '/governance';

export interface CreateOrganisationInput {
  code: string;
  name: string;
  kind: OrgKind;
  model: OrgModel;
  parentId?: string;
  actorName?: string;
}

/**
 * The S2.3 clone-preset modal payload — three user-facing fields only (plan
 * §Slice 2.3); `actorName` rides along for the handler-side audit row.
 */
export interface CreateRoleInput {
  name: string;
  /** Copy this role's grant list onto the new role (omit ⇒ start blank). */
  cloneFromRoleId?: string;
  /** Axis-A `inheritsFrom` edges for the new role (acyclic-validated server-side). */
  inheritsFrom?: string[];
  actorName?: string;
}

export const systemApi = {
  listOrganisations: async (): Promise<GovernanceListResponse<OrganisationUnit>> => {
    const { data } = await apiClient.get<GovernanceListResponse<OrganisationUnit>>(
      `${BASE}/organisations`
    );
    return data;
  },

  createOrganisation: async (input: CreateOrganisationInput): Promise<OrganisationUnit> => {
    const { data } = await apiClient.post<OrganisationUnit>(`${BASE}/organisations`, input);
    return data;
  },

  setOrganisationStatus: async (
    id: string,
    status: GovernanceRecordStatus,
    reason: string,
    actorName?: string
  ): Promise<OrganisationUnit> => {
    const { data } = await apiClient.patch<OrganisationUnit>(`${BASE}/organisations/${id}`, {
      status,
      reason,
      actorName,
    });
    return data;
  },

  listRoles: async (): Promise<GovernanceListResponse<GovernanceRole>> => {
    const { data } = await apiClient.get<GovernanceListResponse<GovernanceRole>>(`${BASE}/roles`);
    return data;
  },

  setRolePermissions: async (
    id: string,
    permissions: string[],
    reason: string,
    actorName?: string
  ): Promise<GovernanceRole> => {
    const { data } = await apiClient.patch<GovernanceRole>(`${BASE}/roles/${id}`, {
      permissions,
      reason,
      actorName,
    });
    return data;
  },

  createRole: async (input: CreateRoleInput): Promise<GovernanceRole> => {
    const { data } = await apiClient.post<GovernanceRole>(`${BASE}/roles`, input);
    return data;
  },

  /** Per-role `inheritsFrom` edit (S2.3) — cycle rejections surface as 422. */
  setRoleHierarchy: async (
    id: string,
    inheritsFrom: string[],
    reason: string,
    actorName?: string
  ): Promise<GovernanceRole> => {
    const { data } = await apiClient.patch<GovernanceRole>(`${BASE}/roles/${id}/hierarchy`, {
      inheritsFrom,
      reason,
      actorName,
    });
    return data;
  },

  listRoleLevels: async (): Promise<GovernanceListResponse<RoleLevel>> => {
    const { data } = await apiClient.get<GovernanceListResponse<RoleLevel>>(`${BASE}/role-levels`);
    return data;
  },

  createRoleLevel: async (name: string, actorName?: string): Promise<RoleLevel> => {
    const { data } = await apiClient.post<RoleLevel>(`${BASE}/role-levels`, { name, actorName });
    return data;
  },

  renameRoleLevel: async (id: string, name: string, actorName?: string): Promise<RoleLevel> => {
    const { data } = await apiClient.patch<RoleLevel>(`${BASE}/role-levels/${id}`, {
      name,
      actorName,
    });
    return data;
  },

  /** No-dep up/down reorder — system-band involvement returns 409. */
  reorderRoleLevel: async (
    id: string,
    direction: 'up' | 'down',
    actorName?: string
  ): Promise<GovernanceListResponse<RoleLevel>> => {
    const { data } = await apiClient.post<GovernanceListResponse<RoleLevel>>(
      `${BASE}/role-levels/${id}/reorder`,
      { direction, actorName }
    );
    return data;
  },

  deleteRoleLevel: async (id: string, actorName?: string): Promise<void> => {
    await apiClient.delete(`${BASE}/role-levels/${id}`, {
      params: actorName ? { actorName } : undefined,
    });
  },

  listUsers: async (): Promise<GovernanceListResponse<GovernanceUser>> => {
    const { data } = await apiClient.get<GovernanceListResponse<GovernanceUser>>(`${BASE}/users`);
    return data;
  },

  setUserStatus: async (
    id: string,
    status: GovernanceUserStatus,
    reason: string,
    actorName?: string
  ): Promise<GovernanceUser> => {
    const { data } = await apiClient.patch<GovernanceUser>(`${BASE}/users/${id}`, {
      status,
      reason,
      actorName,
    });
    return data;
  },

  setUserRoles: async (
    id: string,
    roleIds: string[],
    reason: string,
    actorName?: string
  ): Promise<GovernanceUser> => {
    const { data } = await apiClient.patch<GovernanceUser>(`${BASE}/users/${id}`, {
      roleIds,
      reason,
      actorName,
    });
    return data;
  },

  listAuditLogs: async (query: AuditLogQuery): Promise<GovernanceListResponse<GovernanceAuditLog>> => {
    const params: Record<string, string> = {};
    if (query.dateFrom) params.dateFrom = query.dateFrom;
    if (query.dateTo) params.dateTo = query.dateTo;
    if (query.actor) params.actor = query.actor;
    if (query.module && query.module !== 'all') params.module = query.module;
    const { data } = await apiClient.get<GovernanceListResponse<GovernanceAuditLog>>(
      `${BASE}/audit-logs`,
      { params }
    );
    return data;
  },
};

export default systemApi;
